/**
 * Collection Navigation Bar
 * Handles horizontal scrolling with snap behavior and arrow navigation
 */

(function() {
  'use strict';

  class CollectionNavigationBar {
    constructor(element) {
      this.element = element;
      this.scrollWrapper = null;
      this.prevButton = null;
      this.nextButton = null;
      this.list = null;
      this.showArrows = false;
      this.scrollTimeout = null;
      this.isScrolling = false;
      this.resizeObserver = null;
      this.handleScrollBound = this.handleScroll.bind(this);
      this.scrollPrevBound = this.scrollPrev.bind(this);
      this.scrollNextBound = this.scrollNext.bind(this);
    }

    init() {
      this.scrollWrapper = this.element.querySelector('.collection-nav-bar__scroll-wrapper');
      this.prevButton = this.element.querySelector('.collection-nav-bar__arrow--prev');
      this.nextButton = this.element.querySelector('.collection-nav-bar__arrow--next');
      this.list = this.element.querySelector('.collection-nav-bar__list');
      this.showArrows = this.element.dataset.showArrows === 'true';
      const isStickyEnabled = this.element.dataset.sticky === 'true';

      if (!this.scrollWrapper || !this.list) return;

      // Setze CSS-Variable für Nav-Bar-Höhe (immer)
      this.updateNavBarHeight();

      // Update spacer height if sticky is enabled
      if (isStickyEnabled) {
        this.updateSpacerHeight();
        // Update on resize
        if (window.ResizeObserver) {
          const resizeObserver = new ResizeObserver(() => {
            this.updateSpacerHeight();
            this.updateNavBarHeight();
          });
          resizeObserver.observe(this.element);
        }
      } else {
        // Auch ohne sticky die Höhe bei Resize aktualisieren
        if (window.ResizeObserver) {
          const resizeObserver = new ResizeObserver(() => {
            this.updateNavBarHeight();
          });
          resizeObserver.observe(this.element);
        }
      }

      // Observe header sticky state to sync positioning
      this.observeHeaderStickyState();

      // Initial state
      this.updateArrowStates();

      // Scroll event listener
      this.scrollWrapper.addEventListener('scroll', this.handleScrollBound, { passive: true });

      // Arrow button listeners
      if (this.showArrows) {
        if (this.prevButton) {
          this.prevButton.addEventListener('click', this.scrollPrevBound);
        }
        if (this.nextButton) {
          this.nextButton.addEventListener('click', this.scrollNextBound);
        }
      }

      // Resize observer for responsive updates
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.updateArrowStates();
        });
        this.resizeObserver.observe(this.scrollWrapper);
      }

      // Scroll to current item on load
      this.scrollToCurrentItem();
    }

    destroy() {
      if (this.originalParent && this.element.classList.contains('collection-nav-bar--in-header-group')) {
        this.element.classList.remove('collection-nav-bar--in-header-group');
        this.originalParent.insertBefore(this.element, this.originalNextSibling);
        this.originalParent = null;
        this.originalNextSibling = null;
      }

      if (this.scrollWrapper) {
        this.scrollWrapper.removeEventListener('scroll', this.handleScrollBound);
      }

      if (this.prevButton) {
        this.prevButton.removeEventListener('click', this.scrollPrevBound);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener('click', this.scrollNextBound);
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      if (this.headerObserver) {
        this.headerObserver.disconnect();
      }
      if (this.headerSectionObserver) {
        this.headerSectionObserver.disconnect();
      }
      if (this.headerHeightObserver) {
        this.headerHeightObserver.disconnect();
      }
      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
      }
      if (this.headerGroupResizeObserver) {
        this.headerGroupResizeObserver.disconnect();
      }
      if (this.scrollHandler) {
        document.removeEventListener('scroll', this.scrollHandler);
      }

      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
    }

    handleScroll() {
      if (!this.isScrolling) {
        this.isScrolling = true;
      }

      // Update arrow states during scroll
      this.updateArrowStates();

      // Clear existing timeout
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      // Mark scrolling as ended after a delay
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
      }, 150);
    }

    updateArrowStates() {
      if (!this.scrollWrapper || !this.showArrows) return;

      const { scrollLeft, scrollWidth, clientWidth } = this.scrollWrapper;
      const isAtStart = scrollLeft <= 0;
      const isAtEnd = scrollLeft >= scrollWidth - clientWidth - 1; // -1 for rounding errors

      if (this.prevButton) {
        this.prevButton.disabled = isAtStart;
        this.prevButton.setAttribute('aria-hidden', isAtStart ? 'true' : 'false');
      }

      if (this.nextButton) {
        this.nextButton.disabled = isAtEnd;
        this.nextButton.setAttribute('aria-hidden', isAtEnd ? 'true' : 'false');
      }
    }

    scrollPrev() {
      if (!this.scrollWrapper) return;

      const itemWidth = this.getAverageItemWidth();
      const scrollAmount = itemWidth * 2; // Scroll 2 items at a time

      this.scrollWrapper.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }

    scrollNext() {
      if (!this.scrollWrapper) return;

      const itemWidth = this.getAverageItemWidth();
      const scrollAmount = itemWidth * 2; // Scroll 2 items at a time

      this.scrollWrapper.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }

    getAverageItemWidth() {
      if (!this.list) return 200; // Fallback

      const items = this.list.querySelectorAll('.collection-nav-bar__item');
      if (items.length === 0) return 200;

      let totalWidth = 0;
      items.forEach(item => {
        totalWidth += item.offsetWidth;
      });

      return totalWidth / items.length;
    }

    updateNavBarHeight() {
      if (this.element) {
        const navBarHeight = this.element.offsetHeight;
        document.documentElement.style.setProperty('--nav-bar-height', `${navBarHeight}px`);
      }
    }

    updateSpacerHeight() {
      const spacer = document.querySelector('.collection-nav-bar__spacer');
      if (spacer && this.element) {
        const navBarHeight = this.element.offsetHeight;
        document.documentElement.style.setProperty('--nav-bar-height', `${navBarHeight}px`);
        spacer.style.height = `${navBarHeight}px`;
      }
    }

    observeHeaderStickyState() {
      const header = document.querySelector('header-component');
      const headerGroup = document.querySelector('#header-group');
      if (!header || !headerGroup) return;

      const isCollectionPage = document.body.classList.contains('template-collection') || 
                               window.location.pathname.includes('/collections/') ||
                               (window.Shopify && window.Shopify.theme && window.Shopify.theme.template === 'collection');
      const isStickyEnabled = this.element.dataset.sticky === 'true';

      if (!isCollectionPage && !Shopify?.designMode) {
        this.element.style.display = 'none';
        const spacer = document.querySelector('.collection-nav-bar__spacer');
        if (spacer) {
          spacer.style.display = 'none';
        }
        return;
      }

      if (isCollectionPage && isStickyEnabled) {
        this.originalParent = this.element.parentNode;
        this.originalNextSibling = this.element.nextElementSibling;
        headerGroup.appendChild(this.element);
        this.element.classList.add('collection-nav-bar--in-header-group');
        this.updateSpacerHeight();
        return;
      }

      const updateTopPosition = () => {
        const headerSection = document.querySelector('.header-section');
        const rawScrollTop = document.scrollingElement?.scrollTop ?? window.pageYOffset ?? 0;
        const scrollTop = Math.max(0, rawScrollTop);
        const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
        const headerGroupHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || headerHeight;

        if (!headerSection) {
          this.element.style.top = `${headerGroupHeight}px`;
          return;
        }

        const stickyState = header.dataset.stickyState;
        const stickyMode = header.getAttribute('sticky');
        const isHeaderSticky = header.hasAttribute('sticky') && 
          (stickyMode === 'always' || 
           (stickyMode === 'scroll-up' && stickyState === 'active'));

        if (isHeaderSticky && scrollTop > 1) {
          const headerRect = headerSection.getBoundingClientRect();
          const headerBottom = Math.max(headerGroupHeight, headerRect.bottom);
          const maxTop = window.innerHeight - this.element.offsetHeight;
          this.element.style.top = `${Math.min(headerBottom, maxTop)}px`;
        } else {
          this.element.style.top = `${headerGroupHeight}px`;
        }
      };

      let scrollRafId = null;
      const handleScroll = () => {
        if (scrollRafId !== null) return;
        scrollRafId = requestAnimationFrame(() => {
          scrollRafId = null;
          updateTopPosition();
        });
      };

      this.headerObserver = new MutationObserver(() => {
        updateTopPosition();
      });
      this.headerObserver.observe(header, {
        attributes: true,
        attributeFilter: ['data-sticky-state', 'sticky']
      });

      const headerSection = document.querySelector('.header-section');
      if (headerSection) {
        this.headerSectionObserver = new MutationObserver(() => {
          updateTopPosition();
        });
        this.headerSectionObserver.observe(headerSection, {
          attributes: true,
          attributeFilter: ['class']
        });
      }

      document.addEventListener('scroll', handleScroll, { passive: true });
      this.scrollHandler = handleScroll;

      if (window.ResizeObserver) {
        this.headerHeightObserver = new ResizeObserver(() => {
          updateTopPosition();
          this.updateSpacerHeight();
        });
        this.headerHeightObserver.observe(document.body);
      }

      if (window.ResizeObserver && header) {
        this.headerResizeObserver = new ResizeObserver(() => {
          updateTopPosition();
          this.updateSpacerHeight();
        });
        this.headerResizeObserver.observe(header);
      }

      if (window.ResizeObserver && headerGroup) {
        this.headerGroupResizeObserver = new ResizeObserver(() => {
          updateTopPosition();
          this.updateSpacerHeight();
        });
        this.headerGroupResizeObserver.observe(headerGroup);
      }

      updateTopPosition();
    }

    scrollToCurrentItem() {
      if (!this.scrollWrapper || !this.list) return;

      const currentLink = this.list.querySelector('.collection-nav-bar__link--current');
      if (!currentLink) return;

      const item = currentLink.closest('.collection-nav-bar__item');
      if (!item) return;

      // Wait for layout to be ready
      requestAnimationFrame(() => {
        const itemLeft = item.offsetLeft;
        const itemWidth = item.offsetWidth;
        const containerWidth = this.scrollWrapper.clientWidth;
        const scrollLeft = this.scrollWrapper.scrollLeft;

        // Calculate the position to center the item (or align to start if not enough space)
        const targetScroll = itemLeft - (containerWidth / 2) + (itemWidth / 2);

        // Only scroll if item is not already visible
        const itemRight = itemLeft + itemWidth;
        const visibleLeft = scrollLeft;
        const visibleRight = scrollLeft + containerWidth;

        if (itemLeft < visibleLeft || itemRight > visibleRight) {
          this.scrollWrapper.scrollTo({
            left: Math.max(0, targetScroll),
            behavior: 'smooth'
          });
        }
      });
    }
  }

  // Store instances
  const instances = new WeakMap();

  // Initialize navigation bars
  function initNavigationBar(element) {
    if (instances.has(element)) {
      return instances.get(element);
    }

    const instance = new CollectionNavigationBar(element);
    instance.init();
    instances.set(element, instance);
    return instance;
  }

  // Destroy navigation bar
  function destroyNavigationBar(element) {
    const instance = instances.get(element);
    if (instance) {
      instance.destroy();
      instances.delete(element);
    }
  }

  // Initialize on DOM ready
  function init() {
    // Prüfe ob wir auf einer Collection-Seite sind
    const isCollectionPage = document.body.classList.contains('template-collection') || 
                             window.location.pathname.includes('/collections/');
    
    if (!isCollectionPage) {
      return; // Nicht auf Collection-Seite - nicht initialisieren
    }

    const navBars = document.querySelectorAll('.collection-nav-bar');
    navBars.forEach(nav => {
      initNavigationBar(nav);
    });
  }

  // Handle theme editor events
  document.addEventListener('shopify:section:load', (event) => {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section && section.classList.contains('collection-nav-bar')) {
      // Im Theme Editor immer initialisieren, sonst nur auf Collection-Seiten
      const isCollectionPage = document.body.classList.contains('template-collection') || 
                               window.location.pathname.includes('/collections/') ||
                               Shopify?.designMode; // Theme Editor
      
      if (isCollectionPage || Shopify?.designMode) {
        initNavigationBar(section);
      }
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
    if (section && section.classList.contains('collection-nav-bar')) {
      destroyNavigationBar(section);
    }
  });

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
